import React, { useState, useEffect } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import api from '../../services/api';

interface AgencySetting {
  agency_id: number;
  agency_name: string;
  sys_trip_count: number;
  prior_trips: number;
  real_total: number;
  is_excluded: boolean;
  notes: string;
}

const AgencySettingsPage: React.FC = () => {
  const [rows, setRows] = useState<AgencySetting[]>([]);
  const [edits, setEdits] = useState<Record<number, Partial<AgencySetting>>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api.get('/sales-incentive/agency-settings');
      if (r.data.success) setRows(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const edit = (id: number, field: keyof AgencySetting, value: any) =>
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const get = (row: AgencySetting, field: keyof AgencySetting) =>
    edits[row.agency_id]?.[field] !== undefined ? edits[row.agency_id][field] : row[field];

  const save = async (row: AgencySetting) => {
    setSaving(row.agency_id);
    try {
      await api.post('/sales-incentive/agency-settings', {
        agency_id: row.agency_id,
        prior_trips: Number(get(row, 'prior_trips')),
        is_excluded: Boolean(get(row, 'is_excluded')),
        notes: get(row, 'notes') || '',
      });
      setEdits(prev => { const n = { ...prev }; delete n[row.agency_id]; return n; });
      await fetch();
    } catch (e: any) { alert(e?.response?.data?.detail || 'Save failed'); }
    setSaving(null);
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Agency Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">Set prior trips (pre-system bookings) and exclusion flags per agency.</p>
        </div>
        <button onClick={fetch} className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-lg text-sm">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs font-semibold text-gray-500">
              <th className="px-4 py-2.5 text-left">Agency</th>
              <th className="px-4 py-2.5 text-right">System Trips</th>
              <th className="px-4 py-2.5 text-right text-blue-600">Prior Trips</th>
              <th className="px-4 py-2.5 text-right text-green-600">Real Total</th>
              <th className="px-4 py-2.5 text-center">Excluded</th>
              <th className="px-4 py-2.5 text-left">Notes</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && rows.map(row => {
              const priorVal = Number(get(row, 'prior_trips'));
              const realTotal = row.sys_trip_count + priorVal;
              const isDirty = !!edits[row.agency_id];
              return (
                <tr key={row.agency_id} className={`hover:bg-gray-50 ${isDirty ? 'bg-yellow-50' : ''}`}>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{row.agency_name}</td>
                  <td className="px-4 py-2.5 text-right text-gray-500">{row.sys_trip_count}</td>
                  <td className="px-4 py-2.5 text-right">
                    <input
                      type="number" min={0}
                      value={priorVal}
                      onChange={e => edit(row.agency_id, 'prior_trips', Number(e.target.value))}
                      className="w-20 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-green-700">{realTotal}</td>
                  <td className="px-4 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={Boolean(get(row, 'is_excluded'))}
                      onChange={e => edit(row.agency_id, 'is_excluded', e.target.checked)}
                      className="w-4 h-4 accent-red-500"
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <input
                      type="text"
                      value={String(get(row, 'notes') || '')}
                      onChange={e => edit(row.agency_id, 'notes', e.target.value)}
                      placeholder="Optional note"
                      className="w-48 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400"
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    {isDirty && (
                      <button onClick={() => save(row)} disabled={saving === row.agency_id}
                        className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50">
                        <Save className="w-3 h-3" />
                        {saving === row.agency_id ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No agencies found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Real Total = System Trips + Prior Trips. Commission tiers use Real Total sequence.
        Trips #1–5 earn 2%, #6–10 earn 1.5%, #11+ are excluded from incentive.
      </p>
    </div>
  );
};

export default AgencySettingsPage;
